import { useCallback, useEffect, useRef, useState } from 'react';

export type ViewMode = 'development' | 'production';

export const useViewMode = (): [ViewMode, (viewMode: ViewMode) => void] => {
  const isProduction = process.env.NODE_ENV === 'production';
  const _viewMode: ViewMode =
    (localStorage.getItem('viewMode') as ViewMode) || 'development';

  const [viewMode, _setViewMode] = useState<ViewMode>(
    isProduction
      ? 'production'
      : (document.body.getAttribute('data-view-mode') as ViewMode) || _viewMode,
  );

  const observer = useRef<MutationObserver>(
    new MutationObserver((mutations) => {
      if (isProduction) return;
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'data-view-mode' &&
          mutation.target instanceof HTMLElement &&
          mutation.target.localName === 'body'
        ) {
          _setViewMode(
            mutation.target.getAttribute('data-view-mode') as ViewMode,
          );
        }
      });
    }),
  );

  useEffect(() => {
    const body = document.body;
    if (body.getAttribute('data-view-mode') !== viewMode) {
      body.setAttribute('data-view-mode', viewMode);
    }
  }, [viewMode, document.body]);

  useEffect(() => {
    const body = document.body;
    observer.current.observe(body, {
      attributes: true,
      attributeFilter: ['data-view-mode'],
    });
    return () => {
      observer.current.disconnect();
    };
  }, [observer, document.body]);

  const setViewMode = useCallback(
    (viewMode: ViewMode) => {
      if (isProduction) return;
      try {
        localStorage.setItem('viewMode', viewMode);
      } catch (e) {
        console.warn('Could not save view mode to localStorage', e);
      }
      document.body.setAttribute('data-view-mode', viewMode);
    },
    [document.body],
  );

  return [viewMode, setViewMode];
};
